import { msg } from "@lingui/core/macro";
import {
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  Show,
  untrack,
} from "solid-js";
import {
  Outlet,
  useLocation,
  useNavigate,
  useRouterState,
  useSearch,
} from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

import { useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { CommandPalette } from "@/features/search/components/command-palette";
import { MainContentSkeleton } from "@/components/workspace-skeleton";
import { ShortcutsModal } from "@/components/shortcuts-modal";
import { AppErrorBoundary } from "@/components/error-boundary";
import { useInteractionFlag } from "@fleet/ui";

// Layout module parts
import { AppLayoutContext } from "../context";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";

interface AppLayoutContentProps {
  db: AbstractPowerSyncDatabase | null;
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  // Keyboard-vs-pointer activity tracking (and the `body.keyboard-navigation`
  // class) is owned by <InteractionProvider>; only the sidebar-specific Escape
  // handler remains here.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

export function AppLayoutContent(props: AppLayoutContentProps) {
  const navigate = useNavigate();
  const searchPalette = useSearchPalette();
  const { t } = useI18n();

  // Global overlays addressable via URL (inherited from `_app` layout):
  //   ?palette=1[&q=&filter=]  → Command Palette (global search)
  //   ?modal=shortcuts         → Shortcuts Modal (help)
  // The URL is source of truth: pasting the link reopens exact overlay. It
  // syncs bidirectionally with existing signals (which continue to govern
  // internal logic and keyboard shortcuts). Idempotent: `replace`
  // does not inflate history and preserves other search params.
  const overlaySearch = useSearch({ strict: false }) as () => {
    palette?: boolean;
    modal?: string;
    q?: string;
    scope?: string;
  };

  const handleCloseSearch = () => {
    searchPalette.close();
  };

  const location = useLocation();
  const routerState = useRouterState();

  const isNavigating = () => routerState().status === "pending";

  // SECTION key for page entrance animation: the `routeId` of the active
  // leaf, but collapsing note detail (`/_app/documents/$pageId`) to a single
  // key. Thus, navigating between sibling notes (carousel / click in tree)
  // does NOT re-trigger entrance (`$pageId` changes but section is identical),
  // and TipTap editor is not annoyingly remounted due to wrapper.
  // Any other route uses its routeId as-is → entrance ONCE per section.
  const sectionKey = createMemo(() => {
    const { matches } = routerState();
    const leafId = matches.at(-1)?.routeId ?? "/";
    return leafId.includes("/documents/$pageId") ? "/_app/documents/$pageId" : leafId;
  });

  const theme = useTheme();

  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);
  const [shortcutsOpen, setShortcutsOpen] = createSignal(false);

  // The mobile nav drawer owns horizontal swiping while it is open (see
  // `use-nav-drawer-swipe`); page-level swipe gestures (if any get added later)
  // must stand down via this flag instead of re-deriving "is the drawer open".
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  // Close the mobile sidebar drawer whenever the route changes.
  onMount(() => {
    void location().pathname;
  });

  useAppShortcuts({
    searchPalette,
    location,
    navigate,
    theme,
    t,
    shortcutsOpen,
    setShortcutsOpen,
    setIsSidebarOpen,
  });

  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // ── URL ⇄ overlay signals (palette / shortcuts) ─────────────────────────────
  const wantsPalette = () => overlaySearch().palette === true;
  const wantsShortcuts = () => overlaySearch().modal === "shortcuts";

  // `to: "."` preserves current route and only toggles search-params inherited from
  // `_app` layout; reducer lets TanStack infer type of `prev`.
  const setPaletteUrl = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        palette: open ? true : undefined,
        // On close, also clear search state from the URL.
        ...(open ? {} : { q: undefined, scope: undefined }),
      }),
      replace: true,
    });
  };
  const setShortcutsUrl = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        modal: open ? ("shortcuts" as const) : undefined,
      }),
      replace: true,
    });
  };

  // URL → signals (link pegado, back/forward).
  // NOTE (CC-16): reading state is wrapped in `untrack` — if effect also
  // tracks searchPalette.isOpen()/shortcutsOpen(), opening overlay state-first
  // (⌘K, "?") re-triggers it with STILL-stale search-params (navigate is async)
  // and CLOSES it instantly. It should only react to EXTERNAL URL changes.
  createEffect(() => {
    const want = wantsPalette();
    untrack(() => {
      if (want !== searchPalette.isOpen()) {
        if (want) {
          searchPalette.open();
        } else {
          searchPalette.close();
        }
      }
    });
  });
  createEffect(() => {
    const want = wantsShortcuts();
    if (want !== untrack(shortcutsOpen)) {
      setShortcutsOpen(want);
    }
  });

  // signals → URL (keyboard shortcuts, header buttons).
  createEffect(() => {
    if (searchPalette.isOpen() !== wantsPalette()) {
      setPaletteUrl(searchPalette.isOpen());
    }
  });
  createEffect(() => {
    if (shortcutsOpen() !== wantsShortcuts()) {
      setShortcutsUrl(shortcutsOpen());
    }
  });

  return (
    <AppBackground
      class="flex h-dvh text-foreground overflow-hidden"
      isBlur={theme.isBlur()}
      patternType={theme.tokens()?.patternType}
      patternOpacity={theme.tokens()?.patternOpacity}
      glow0={theme.tokens()?.glow0}
      glow1={theme.tokens()?.glow1}
      glow2={theme.tokens()?.glow2}
    >
      {/* Skip-to-content: primer tabbable, oculto hasta recibir foco (a11y). */}
      <a
        href="#main-content"
        class="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[200] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent"
      >
        {t(msg`Skip to content`)}
      </a>

      <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

      {/* ── Main content ───────────────────────────────────────── */}
      <main
        id="main-content"
        tabindex="-1"
        class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)] focus:outline-none"
      >
        <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
        <OfflineBanner />
        <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
          <AppLayoutContext.Provider value={{}}>
            <div class="flex flex-1 flex-col min-h-0 h-full">
              {/* The Outlet's routes read PowerSync themselves; until the
                  local DB is ready there is no data to render, so show a
                  granular content skeleton in this region only. */}
              {/* ErrorBoundary de la región de datos principal: aísla un fallo
                  de cualquier ruta hija (editor, árbol…) del shell — el header,
                  sidebar y navegación siguen vivos para reintentar/recargar. */}
              <AppErrorBoundary variant="page" label="main-content">
                <Show when={props.db} fallback={<MainContentSkeleton />}>
                  {/* Animación de entrada de página, UNA sola vez por SECCIÓN de
                      ruta (canon "estilo perfil"). `keyed` por `sectionKey()`
                      remonta este wrapper al cambiar de sección, re-disparando
                      `slide-in-up`; las sub-rutas (p. ej. notas hermanas)
                      colapsan a la misma clave y NO re-animan. Este div es
                      SEPARADO del que recibe el `transform` del carrusel (ese
                      vive dentro de NotePageView), así no pelean. `will-change`
                      se limpia al terminar para no fijar capas GPU. */}
                  <Show when={sectionKey()} keyed>
                    {(_section) => (
                      <div
                        class="flex flex-1 flex-col min-h-0 h-full motion-safe:animate-slide-in-up motion-safe:will-change-[transform,opacity]"
                        on:animationend={(event) => {
                          if (event.target === event.currentTarget) {
                            event.currentTarget.style.willChange = "auto";
                          }
                        }}
                      >
                        <Outlet />
                      </div>
                    )}
                  </Show>
                </Show>
              </AppErrorBoundary>
            </div>
          </AppLayoutContext.Provider>
        </div>
      </main>

      <ShortcutsModal
        isOpen={shortcutsOpen()}
        onClose={() => {
          setShortcutsOpen(false);
        }}
      />

      {/* Command Palette — rendered last to avoid blur compositor flash */}
      <CommandPalette isOpen={searchPalette.isOpen()} onClose={handleCloseSearch} />
    </AppBackground>
  );
}
